import type { MechanicDefinition } from "./types";

export const reputation: MechanicDefinition = {
  id: "reputation",
  name: "Reputation & Disposition",
  description: "Track standing with factions AND individual NPCs. Suspicion/trust levels affect NPC behavior and social interaction outcomes.",
  icon: "Star",
  category: "systems",
  aiHints: {
    purpose: "Tracks faction reputation and individual NPC trust/suspicion to gate content and shape dialogue.",
    whenToUse: "Worlds with multiple factions, political intrigue, or where NPC relationships matter.",
    synergies: ["dialogue_system", "morality", "influence", "reactive_world"],
    conflicts: [],
    prerequisites: ["Faction entities should exist in the world"],
    tags: ["social", "factions", "npc", "trust", "suspicion", "diplomacy"],
    complexity: 3,
    exampleConfig: { scale: "simple", decay: false, npc_disposition: true, default_suspicion: "medium", check_style: "algorithmic" },
    genrePresets: {
      "political_intrigue": { scale: "detailed", decay: true, npc_disposition: true, default_suspicion: "high", check_style: "algorithmic" },
      "friendly_adventure": { scale: "simple", decay: false, npc_disposition: true, default_suspicion: "low", check_style: "narrative" },
    },
    stateVariables: ["_reputation_scale", "_npc_suspicion", "_default_suspicion"],
  },
  configSchema: [
    {
      key: "scale",
      label: "Reputation Scale",
      type: "select",
      options: [
        { value: "simple", label: "Simple (-3 to +3)" },
        { value: "detailed", label: "Detailed (-100 to +100)" },
      ],
      default: "simple",
    },
    {
      key: "decay",
      label: "Reputation Decay",
      type: "boolean",
      default: false,
      description: "Reputation slowly drifts to neutral over time",
    },
    {
      key: "npc_disposition",
      label: "NPC Disposition Tracking",
      type: "boolean",
      default: true,
      description: "Individual NPCs have trust/suspicion levels that affect dialogue and cooperation",
    },
    {
      key: "default_suspicion",
      label: "Default NPC Suspicion",
      type: "select",
      options: [
        { value: "low", label: "Low (NPCs are generally trusting)" },
        { value: "medium", label: "Medium (NPCs are cautious)" },
        { value: "high", label: "High (NPCs are distrustful)" },
      ],
      default: "medium",
    },
    {
      key: "check_style",
      label: "Social Check Style",
      type: "select",
      options: [
        { value: "algorithmic", label: "Algorithmic (dice + modifiers + randomness)" },
        { value: "narrative", label: "Narrative (AI-weighted with soft rolls)" },
      ],
      default: "algorithmic",
    },
  ],
  systemPromptSection: (config) => {
    const scale = config.scale || "simple";
    const decay = config.decay || false;
    const npcDisp = config.npc_disposition !== false;
    const defaultSus = config.default_suspicion || "medium";
    const style = config.check_style || "algorithmic";

    const susMap: Record<string, string> = {
      low: "Most NPCs start with suspicion 15-30 (trusting). Only criminals, enemies, or paranoid types start higher.",
      medium: "Most NPCs start with suspicion 40-60 (cautious). Guards, merchants, innkeepers are wary but approachable.",
      high: "Most NPCs start with suspicion 60-80 (distrustful). Trust must be EARNED. Only close allies start below 50.",
    };

    let prompt = `## REPUTATION & DISPOSITION (Hard Mechanic)
### Faction Reputation
- Scale: ${scale === "simple" ? "-3 (Hated) to +3 (Revered)" : "-100 (Hated) to +100 (Revered)"}
${decay ? "- Reputation slowly decays toward neutral if not maintained." : ""}
- Use the adjust_reputation tool for all faction reputation changes.
- Player actions that help or harm a faction adjust reputation.
- NPCs react based on faction reputation: hostile at low, friendly at high.
- Some quests or areas may require minimum reputation with a faction.
- Mention reputation changes when they occur.

### NPC-Level Reputation
- Individual NPCs can also have personal reputation scores (use adjust_reputation with an NPC name instead of faction).
- NPC personal reputation overrides faction reputation for that specific NPC.
- An NPC you've personally helped will stay friendly even if their faction dislikes you.`;

    if (npcDisp) {
      prompt += `

### NPC SUSPICION & TRUST (0-100)
Every NPC also has a SUSPICION LEVEL tracked in _npc_suspicion state (keyed by name).
- 0-20: TRUSTING — shares secrets, offers aid freely.
- 21-40: FRIENDLY — open, cooperative, shares rumors.
- 41-60: GUARDED — basic answers only, won't reveal sensitive info.
- 61-80: SUSPICIOUS — short answers, deflects, may lie or refuse.
- 81-100: HOSTILE/FEARFUL — refuses to talk, calls guards, flees, or attacks.

Default: ${susMap[defaultSus]}
- Faction reputation modifies initial suspicion by ±10-30.

### SOCIAL CHECKS (${style === "algorithmic" ? "Algorithmic" : "Narrative"})
**CRITICAL**: When the player attempts persuasion, deception, intimidation, bribery, charm, or social manipulation:
1. ALWAYS use the social_check tool BEFORE narrating the NPC's response.
2. NEVER auto-succeed social interactions — the dice + suspicion determine outcomes.
3. Approach effects:
   - Persuasion: best on friendly/guarded NPCs, reduces suspicion on success.
   - Deception: high risk — critical failure INCREASES suspicion +20-30.
   - Intimidation: can force compliance but INCREASES suspicion.
   - Bribery: effective on greedy NPCs, offensive to honorable ones.
   - Charm/Flattery: low-risk, gradual. Small suspicion reduction even on failure.
   - Empathy/Aid: strongest trust builder.
   - Coercion: forces compliance but suspicion spikes to 90+.

### MANDATORY BEHAVIOR
- When invoking invoke_character_agent, the NPC's suspicion MUST influence dialogue.
- If suspicion > 60, NPC should refuse info, deflect, or mislead UNLESS social_check succeeds.
- NPCs gossip: deceiving one merchant may raise suspicion +10-20 for nearby merchants.
- Track changes with update_npc_suspicion after significant interactions.
- First meeting: set initial suspicion via update_npc_suspicion based on context and default level.`;
    }

    return prompt;
  },
  widgets: [
    {
      id: "reputation_tab",
      type: "sidebar_tab",
      label: "Reputation",
      icon: "Shield",
      component: "ReputationWidget",
      dataBindings: { _reputation_scale: "variables._reputation_scale" },
      visibility: "when_data",
      priority: 60,
    },
  ],
  initializeState: (config) => ({
    _reputation_scale: config.scale || "simple",
    ...(config.npc_disposition !== false ? {
      _npc_suspicion: {},
      _default_suspicion: config.default_suspicion || "medium",
    } : {}),
  }),
  onEvent: (event) => {
    if (event.type === "quest_complete" && event.data.faction) {
      const change = event.data.reputation_change || 1;
      return {
        narrativeNotes: [`📈 Quest completion affects ${event.data.faction} reputation: +${change}`],
      };
    }
    return null;
  },
  postTurnHook: (context) => {
    const updates: Record<string, any> = {};
    let changed = false;

    // Faction reputation decay
    if (context.config.decay) {
      const scale = context.config.scale || "simple";
      const decayAmount = scale === "simple" ? 0.1 : 1;
      const vars = context.state.variables || {};
      for (const [key, val] of Object.entries(vars)) {
        if (key.startsWith("reputation_") && typeof val === "number" && val !== 0) {
          const decayed = val > 0
            ? Math.max(0, val - decayAmount)
            : Math.min(0, val + decayAmount);
          updates[key] = decayed;
          changed = true;
        }
      }
    }

    // NPC suspicion decay
    if (context.config.npc_disposition !== false) {
      const suspicions = context.state.variables?._npc_suspicion || {};
      const npcUpdates: Record<string, number> = {};
      for (const [npc, level] of Object.entries(suspicions) as [string, number][]) {
        if (typeof level !== "number") continue;
        if (level > 35) {
          npcUpdates[npc] = Math.max(30, level - 2);
          changed = true;
        } else if (level < 25) {
          npcUpdates[npc] = Math.min(25, level + 1);
          changed = true;
        } else {
          npcUpdates[npc] = level;
        }
      }
      if (Object.keys(npcUpdates).length > 0) {
        updates._npc_suspicion = npcUpdates;
      }
    }

    if (!changed) return null;
    return { stateUpdates: updates };
  },
  tools: [
    {
      type: "function",
      function: {
        name: "adjust_reputation",
        description: "Change the player's reputation with a faction or individual NPC. Use for both faction-level and NPC-level reputation.",
        parameters: {
          type: "object",
          properties: {
            target: { type: "string", description: "Name of the faction or individual NPC" },
            change: { type: "number", description: "Amount to change (positive or negative)" },
            reason: { type: "string", description: "What caused the reputation change" },
            target_type: { type: "string", enum: ["faction", "npc"], description: "Whether this is a faction or individual NPC reputation change" },
          },
          required: ["target", "change", "reason"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "social_check",
        description: "MANDATORY: Roll a social interaction check before any persuasion, deception, intimidation, bribery, charm, or social manipulation attempt. Factors NPC suspicion, player CHA, approach type, and random chance.",
        parameters: {
          type: "object",
          properties: {
            npc_name: { type: "string", description: "NPC being interacted with" },
            approach: { type: "string", enum: ["persuasion", "deception", "intimidation", "bribery", "charm", "empathy", "coercion"], description: "Type of social approach" },
            difficulty: { type: "string", enum: ["trivial", "easy", "moderate", "hard", "extreme"], description: "How difficult this request is for the NPC" },
            context: { type: "string", description: "What the player is trying to achieve" },
          },
          required: ["npc_name", "approach", "difficulty", "context"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "update_npc_suspicion",
        description: "Update an NPC's suspicion level after a significant interaction or first meeting. Negative = trust gained, positive = more suspicious.",
        parameters: {
          type: "object",
          properties: {
            npc_name: { type: "string", description: "NPC name" },
            change: { type: "number", description: "Suspicion change (-100 to +100). Use absolute value for initial setup." },
            reason: { type: "string", description: "Why suspicion changed" },
            set_absolute: { type: "boolean", description: "If true, sets suspicion TO this value instead of adding" },
          },
          required: ["npc_name", "change", "reason"],
        },
      },
    },
  ],
};
